import { handleSuccessfulSubmission } from "common";
import { produce } from "immer";
import { isString } from "lodash";
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { GiftOffer, LineItemType, Payment, PaymentMethod, Purchase, PurchaseStatus } from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { getAuthNetPurchasePayload, handlePaymentError } from "../../helpers/authNetPaymentHelper";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { hasCreditCardLineItem, isReservePurchaseProcess } from "../../models/CreatePurchaseState";
import { getCreditCardLineItem, isHold, needsPrepare } from "../../models/Purchase";
import { invalidateSellerBalance, isAuthorizeNetEnabled } from "../../models/Seller";
import {
    clearDraftPurchasesCache,
    CreatePurchaseState,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { updateCache } from "../../utils/updateCache";
import { XolaFormik } from "../Common/XolaFormik";
import { PaymentInputRef, TokenizePaymentParams } from "../Input/PaymentInputRef";
import { ContentLayout } from "../Layout/ContentLayout";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { CheckoutPurchaseScreenFooter } from "./CheckoutPurchaseScreenFooter";
import { CreatePurchaseCustomerFields } from "./CreatePurchaseCustomerFields";
import { CreatePurchaseGiftFields } from "./CreatePurchaseGiftFields";
import { CreatePurchaseInternalNoteFields } from "./CreatePurchaseInternalNoteFields";
import { CreatePurchasePaymentDetails } from "./CreatePurchasePaymentDetails";
import { CreatePurchaseSidebar } from "./CreatePurchaseSidebar";
import {
    getPayload,
    getPostCreateBookingAction,
    isPurchaseNotFoundError,
    resetPurchase,
} from "./helpers/PurchaseCreateHelpers";
import { useCheckoutGiftPurchaseInitialValues } from "./hooks/useCheckoutPurchaseInitialValues";
import { useGiftCheckoutValidations } from "./hooks/useCheckoutPurchaseScreenValidations";
import { useUpsertPurchase } from "./hooks/useUpsertPurchase";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";

interface CheckoutPurchaseScreenProps {
    product: GiftOffer;
    onBack: () => void;
}

export const CheckoutGiftPurchaseScreen = ({ product, onBack }: CheckoutPurchaseScreenProps) => {
    const seller = useSeller();
    const history = useHistory();
    const paymentInputRef = useRef<PaymentInputRef>(null);

    const purchaseBalance = useCreatePurchaseStore((state) => state.balance);
    const showCustomerFields = useCustomerDetailsStore((state) => state.showCustomerFields);
    const { resetState, setIsEMV } = useCreatePurchaseStore.getState().actions;
    const validationSchema = useGiftCheckoutValidations();

    const [isSuccess, setIsSuccess] = useState(false);

    const shouldShowPaymentMethods = purchaseBalance !== 0;

    const { mutateAsync: upsertPurchase, isLoading } = useUpsertPurchase();

    const initialValues = useCheckoutGiftPurchaseInitialValues();

    const getPayloadForPaymentGateway = async (payload: CreatePurchaseState) => {
        const { cardInputMethod } = useCreatePurchaseStore.getState();
        const { paymentLineItem } = useCreatePurchaseStore.getState();
        const paymentStatus = paymentLineItem.status;

        if (isAuthorizeNetEnabled(seller) && hasCreditCardLineItem(payload)) {
            if (isString(paymentLineItem.payment.card)) {
                return payload;
            }

            const response = (await paymentInputRef.current?.tokenize()) as TokenizePaymentParams;
            if (!response) {
                return;
            }

            return getAuthNetPurchasePayload(response, payload, paymentStatus, cardInputMethod);
        }

        return payload;
    };

    const handleSubmit = async (values: CheckoutFormValues) => {
        const { paymentLineItem } = useCreatePurchaseStore.getState();

        const payload = getPayload(values) as CreatePurchaseState;

        let purchasePayload = await getPayloadForPaymentGateway(payload);
        if (!purchasePayload) {
            return;
        }

        const { dueNow = 0 } = usePurchaseResponseStore.getState().purchase;

        // Added condition to get balance due equal to 0 when using coupon with 100% discount, and deposit field has value more than 0
        const paymentInputParams = { amountDue: dueNow };
        const paymentMethod = paymentLineItem.payment.method;
        const isCashPayment = paymentMethod === PaymentMethod.CASH;
        const isPayLater = paymentMethod === PaymentMethod.LATER;
        const isReservePurchase = isReservePurchaseProcess(paymentMethod, dueNow, paymentLineItem.status);
        const shouldPrepare = (!isPayLater && paymentInputRef.current && needsPrepare()) || isCashPayment;

        if (shouldPrepare && !isReservePurchase) {
            const response = await paymentInputRef.current?.prepare?.(paymentInputParams);
            if (!response) {
                return;
            }

            if (isCashPayment) {
                purchasePayload = produce(purchasePayload, (draft) => {
                    const paymentLineItem = draft.lineItems.find(
                        (lineItem) =>
                            lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.CASH,
                    );
                    if (paymentLineItem) {
                        paymentLineItem.payment = response as Payment;
                    }
                });
            }
        }

        useCreatePurchaseStore.setState({ isSubmitting: true });

        try {
            const createdPurchase = await upsertPurchase(purchasePayload);

            const creditCardPaymentLineItem = getCreditCardLineItem(createdPurchase);
            const clientSecret = creditCardPaymentLineItem?.payment?.paymentIntent?.clientSecret;
            if (clientSecret) {
                try {
                    await paymentInputRef.current?.confirm({ clientSecret });
                } catch (error) {
                    if (isHold(createdPurchase)) {
                        // Release the order if the payment failed otherwise the availability will be held up
                        await api.delete(`/purchases/${createdPurchase.id}`);
                    } else {
                        throw error;
                    }

                    return;
                }
            }

            if (isHold(createdPurchase) && !isAuthorizeNetEnabled(seller)) {
                const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${createdPurchase.id}`, {
                    status: PurchaseStatus.COMMITTED,
                    lineItems: creditCardPaymentLineItem ? [{ id: creditCardPaymentLineItem.id }] : [],
                });
                const { isEMV } = useCreatePurchaseStore.getState();
                if (isEMV) {
                    setIsEMV(false);
                }

                handlePaymentSuccess(updatedPurchase);
            } else {
                handlePaymentSuccess(createdPurchase);
            }

            if ((createdPurchase.status = PurchaseStatus.COMMITTED)) {
                setIsSuccess(true);
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    draft.status = PurchaseStatus.COMMITTED;
                });
                // Removing the search query as we are redirecting to the purchase page as we need show the latest purchase
                usePurchaseSearchStore.getState().setSearchQuery("");

                // Clear draft purchases cache since we don't need after completion.
                clearDraftPurchasesCache();
            }
        } catch (error: any) {
            // Handles the rare case when the seller was afk for long enough on this screen for the purchase draft to be deleted, refer to the X2-9369.
            if (isPurchaseNotFoundError(error)) {
                resetPurchase();
            }

            // This is required to open additional billing details modal in case there is an error while making authorize.net payment
            handlePaymentError(error, seller, paymentInputRef);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const handlePaymentSuccess = (createdPurchase: Purchase) => {
        // Do these actions only if the purchase is not partner experience purchase
        updateCache("purchases", createdPurchase);
        updateCache("purchaseItems", createdPurchase.items);
        const { paymentLineItem } = useCreatePurchaseStore.getState();
        if (paymentLineItem.payment.method === PaymentMethod.CREDIT_CARD) {
            invalidateSellerBalance();
        }

        if ((createdPurchase.status = PurchaseStatus.COMMITTED)) {
            setIsSuccess(true);
        }

        routeToPurchases(createdPurchase);
    };

    const routeToPurchases = async (purchase: Purchase) => {
        if (!purchase) {
            return;
        }

        handleSuccessfulSubmission(() => {
            history.push(getPostCreateBookingAction(seller, purchase));
            resetState();
        });
    };

    const handleAdditionalBillingSubmit = async (
        tokenizePaymentParams: TokenizePaymentParams,
        values: CheckoutFormValues,
    ) => {
        const { paymentLineItem } = useCreatePurchaseStore.getState();
        const paymentStatus = paymentLineItem.status;
        const payload = getPayload(values) as CreatePurchaseState;
        const purchasePayload = getAuthNetPurchasePayload(tokenizePaymentParams, payload, paymentStatus);
        let createdPurchase = null;

        try {
            createdPurchase = await upsertPurchase(purchasePayload);
            handlePaymentSuccess(createdPurchase);
        } catch (error: any) {
            paymentInputRef.current?.onPaymentError?.(error.response?.data?.global);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    return (
        <div className="grid h-full grid-cols-12">
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <div className="space-y-7">
                    <h3>Gift Details</h3>
                    {/* For certain gift offers there's no lineItem before the fist successful dryRun,
                     So preventing the form render as it's dependent on lineItems  */}
                    <XolaFormik<CheckoutFormValues>
                        enableReinitialize
                        enablePrompt={false}
                        initialValues={initialValues}
                        validationSchema={object().shape(validationSchema)}
                        onSubmit={handleSubmit}
                    >
                        <div className="space-y-4">
                            <CreatePurchaseGiftFields product={product} />

                            <hr className="!my-8 text-gray-light" />

                            <div className="grid grid-cols-12">
                                <div className="col-span-12">
                                    {showCustomerFields && <CreatePurchaseCustomerFields product={product} />}

                                    {shouldShowPaymentMethods && (
                                        <CreatePurchasePaymentDetails
                                            ref={paymentInputRef}
                                            validate={() => paymentInputRef.current?.getError?.()}
                                            onAdditionalDetailsSubmit={handleAdditionalBillingSubmit}
                                        />
                                    )}
                                </div>
                            </div>

                            <hr className="!mt-4 !mb-8 text-gray-light" />

                            <div>
                                <CreatePurchaseInternalNoteFields />
                            </div>

                            <CheckoutPurchaseScreenFooter
                                isLoading={isLoading}
                                secondaryText="Back"
                                isSuccess={isSuccess}
                                onCancel={onBack}
                            />

                            <ScrollToFieldError />
                        </div>
                    </XolaFormik>
                </div>
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
        </div>
    );
};
